const addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
  let prevCanPrev = null;
  let prevCanNext = null;
  const togglePrevNextBtnsState = () => {
    const canPrev = emblaApi.canScrollPrev();
    const canNext = emblaApi.canScrollNext();
    if (canPrev === prevCanPrev && canNext === prevCanNext) return;
    prevCanPrev = canPrev;
    prevCanNext = canNext;
    prevBtn.toggleAttribute("disabled", !canPrev);
    nextBtn.toggleAttribute("disabled", !canNext);
  };
  emblaApi.on("select", togglePrevNextBtnsState).on("init", togglePrevNextBtnsState).on("reinit", togglePrevNextBtnsState);
  return () => {
    prevBtn.removeAttribute("disabled");
    nextBtn.removeAttribute("disabled");
  };
};
const addPrevNextBtnsClickHandlersSSR = (emblaApi, prevBtn, nextBtn) => {
  const scrollPrev = () => {
    var _a;
    emblaApi.scrollPrev();
    if ((_a = emblaApi.plugins()) == null ? void 0 : _a.autoplay) {
      requestAnimationFrame(() => {
        var _a2;
        (_a2 = emblaApi.plugins()) == null ? void 0 : _a2.autoplay.reset();
      });
    }
  };
  const scrollNext = () => {
    var _a;
    emblaApi.scrollNext();
    if ((_a = emblaApi.plugins()) == null ? void 0 : _a.autoplay) {
      requestAnimationFrame(() => {
        var _a2;
        (_a2 = emblaApi.plugins()) == null ? void 0 : _a2.autoplay.reset();
      });
    }
  };
  prevBtn.addEventListener("click", scrollPrev, false);
  nextBtn.addEventListener("click", scrollNext, false);
  const removeTogglePrevNextBtnsActive = addTogglePrevNextBtnsActive(
    emblaApi,
    prevBtn,
    nextBtn
  );
  return () => {
    removeTogglePrevNextBtnsActive();
    prevBtn.removeEventListener("click", scrollPrev, false);
    nextBtn.removeEventListener("click", scrollNext, false);
  };
};
const addInstagramDotBtnsAndClickHandlers = (emblaApi, dotsNode) => {
  let dotNodes = [];
  const addDotBtnsWithClickHandlers = () => {
    const dotCount = emblaApi.scrollSnapList().length;
    dotsNode.innerHTML = Array(dotCount).fill(0).map(
      (_, i) => `<button class="embla__dot${i === 0 ? " embla__dot--selected" : ""}" type="button" aria-label="Go to slide ${i + 1}"><div class="embla__dot-inner"></div></button>`
    ).join("");
    dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
    dotNodes.forEach((dotNode, index) => {
      dotNode.addEventListener("click", () => emblaApi.scrollTo(index), false);
    });
  };
  const toggleDotBtnsActive = () => {
    const previous = emblaApi.previousScrollSnap();
    const selected = emblaApi.selectedScrollSnap();
    if (dotNodes[previous]) {
      dotNodes[previous].classList.remove("embla__dot--selected");
    }
    if (dotNodes[selected]) {
      dotNodes[selected].classList.add("embla__dot--selected");
    }
  };
  let isInitialized = false;
  emblaApi.on("init", addDotBtnsWithClickHandlers);
  emblaApi.on("reinit", addDotBtnsWithClickHandlers);
  emblaApi.on("init", () => {
    if (!isInitialized) {
      emblaApi.on("select", toggleDotBtnsActive);
      isInitialized = true;
    }
    toggleDotBtnsActive();
  });
  return () => {
    dotsNode.innerHTML = "";
  };
};
const addAutoplayProgressListeners = (emblaApi, getActiveBarNode) => {
  const updateProgress = () => {
    var _a;
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (!autoplay) return;
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const inner = barNode.querySelector(".embla__bar-inner");
    if (!inner) return;
    const timeUntilNext = autoplay.timeUntilNext();
    const delay = autoplay.options.delay;
    if (timeUntilNext === null) return;
    const progress = 1 - timeUntilNext / delay;
    inner.style.setProperty("--autoplay-progress", progress);
    requestAnimationFrame(updateProgress);
  };
  const resetProgress = () => {
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const inner = barNode.querySelector(".embla__bar-inner");
    if (inner) {
      inner.style.setProperty("--autoplay-progress", 0);
    }
  };
  updateProgress();
  emblaApi.on("autoplay:timerset", updateProgress).on("autoplay:timerstopped", resetProgress).on("autoplay:reset", resetProgress);
  return () => {
    emblaApi.off("autoplay:timerset", updateProgress).off("autoplay:timerstopped", resetProgress);
  };
};
const addBarsBtnsAndClickHandlersSSR = (emblaApi, barsNode, originalSlideCount = null) => {
  let barNodes = [];
  let selectedNode = 0;
  let scrollHandlers = [];
  const getBarCount = () => originalSlideCount || barNodes.length || emblaApi.scrollSnapList().length;
  const setupClickHandlers = () => {
    var _a;
    barNodes.forEach((barNode, index) => {
      if (scrollHandlers[index]) {
        barNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
    barNodes = Array.from(barsNode.querySelectorAll(".embla__bar"));
    const scrollTo = (index) => {
      var _a2;
      const autoplay2 = (_a2 = emblaApi.plugins()) == null ? void 0 : _a2.autoplay;
      emblaApi.scrollTo(index);
      requestAnimationFrame(() => {
        if (autoplay2) {
          autoplay2.reset();
        }
      });
    };
    barNodes.forEach((barNode, index) => {
      const handler = () => scrollTo(index);
      scrollHandlers.push(handler);
      barNode.addEventListener("click", handler);
    });
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (autoplay) {
      barsNode.classList.add("autoplay");
      barsNode.classList.remove("non-autoplay");
      if (barNodes[selectedNode]) {
        barNodes[selectedNode].classList.add("embla__bar--selected");
      }
      addAutoplayProgressListeners(
        emblaApi,
        () => barNodes[selectedNode]
      );
    } else {
      barsNode.classList.add("non-autoplay");
      barsNode.classList.remove("autoplay");
    }
  };
  const toggleBarBtnsActive = () => {
    const selected = emblaApi.selectedScrollSnap();
    const previous = emblaApi.previousScrollSnap();
    const barCount = getBarCount();
    const mappedSelected = originalSlideCount ? selected % barCount : selected;
    const mappedPrevious = originalSlideCount ? previous % barCount : previous;
    if (barNodes[mappedPrevious]) {
      barNodes[mappedPrevious].classList.remove("embla__bar--selected");
    }
    if (barNodes[mappedSelected]) {
      barNodes[mappedSelected].classList.add("embla__bar--selected");
    }
    selectedNode = mappedSelected;
  };
  let isInitialized = false;
  emblaApi.on("init", setupClickHandlers);
  emblaApi.on("reinit", setupClickHandlers);
  emblaApi.on("init", () => {
    if (!isInitialized) {
      emblaApi.on("select", toggleBarBtnsActive);
      isInitialized = true;
    }
    toggleBarBtnsActive();
  });
  return () => {
    barNodes.forEach((barNode, index) => {
      if (scrollHandlers[index]) {
        barNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
  };
};
const addAutoplayProgressDotListeners = (emblaApi, getActiveBarNode) => {
  const updateProgress = () => {
    var _a;
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (!autoplay) return;
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const dot = barNode.querySelector(".embla__dot-inner, .embla__dot__inner");
    if (!dot) return;
    const timeUntilNext = autoplay.timeUntilNext();
    const delay = autoplay.options.delay;
    if (timeUntilNext === null) return;
    const progress = 1 - timeUntilNext / delay;
    dot.style.setProperty("--autoplay-progress", progress);
    requestAnimationFrame(updateProgress);
  };
  const resetProgress = () => {
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const dot = barNode.querySelector(".embla__dot-inner, .embla__dot__inner");
    dot.style.setProperty("--autoplay-progress", 0);
  };
  updateProgress();
  emblaApi.on("autoplay:timerset", updateProgress).on("autoplay:timerstopped", resetProgress).on("autoplay:reset", resetProgress);
  return () => {
    emblaApi.off("autoplay:timerset", updateProgress).off("autoplay:timerstopped", resetProgress);
  };
};
const addDotsBtnsAndClickHandlersSSR = (emblaApi, dotsNode, originalSlideCount = null) => {
  let dotNodes = [];
  let selectedNode = 0;
  let scrollHandlers = [];
  const getDotCount = () => originalSlideCount || dotNodes.length || emblaApi.scrollSnapList().length;
  const setupClickHandlers = () => {
    var _a;
    dotNodes.forEach((dotNode, index) => {
      if (scrollHandlers[index]) {
        dotNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
    dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
    const scrollTo = (index) => {
      var _a2;
      const autoplay2 = (_a2 = emblaApi.plugins()) == null ? void 0 : _a2.autoplay;
      emblaApi.scrollTo(index);
      requestAnimationFrame(() => {
        if (autoplay2) {
          autoplay2.reset();
        }
      });
    };
    dotNodes.forEach((dotNode, index) => {
      const handler = () => scrollTo(index);
      scrollHandlers.push(handler);
      dotNode.addEventListener("click", handler);
    });
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (autoplay) {
      dotsNode.classList.add("autoplay");
      dotsNode.classList.remove("non-autoplay");
      if (dotNodes[selectedNode]) {
        dotNodes[selectedNode].classList.add("embla__dot--selected");
      }
      addAutoplayProgressDotListeners(
        emblaApi,
        () => dotNodes[selectedNode]
      );
    } else {
      dotsNode.classList.add("non-autoplay");
      dotsNode.classList.remove("autoplay");
    }
  };
  const toggleDotBtnsActive = () => {
    const selected = emblaApi.selectedScrollSnap();
    const previous = emblaApi.previousScrollSnap();
    const dotCount = getDotCount();
    const mappedSelected = originalSlideCount ? selected % dotCount : selected;
    const mappedPrevious = originalSlideCount ? previous % dotCount : previous;
    if (dotNodes[mappedPrevious]) {
      dotNodes[mappedPrevious].classList.remove("embla__dot--selected");
    }
    if (dotNodes[mappedSelected]) {
      dotNodes[mappedSelected].classList.add("embla__dot--selected");
    }
    selectedNode = mappedSelected;
  };
  let isInitialized = false;
  emblaApi.on("init", setupClickHandlers);
  emblaApi.on("reinit", setupClickHandlers);
  emblaApi.on("init", () => {
    if (!isInitialized) {
      emblaApi.on("select", toggleDotBtnsActive);
      isInitialized = true;
    }
    toggleDotBtnsActive();
  });
  return () => {
    dotNodes.forEach((dotNode, index) => {
      if (scrollHandlers[index]) {
        dotNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
  };
};
const addLayoutShiftPrevention = (emblaApi, emblaWrapper) => {
  if (!emblaApi || !emblaWrapper) {
    return () => {
    };
  }
  const onInit = () => {
    var _a, _b;
    emblaWrapper.classList.add("is-initialized");
    if (emblaApi.slideNodes().length <= 1) {
      emblaWrapper.classList.add("is-single-slide");
      (_a = emblaApi.containerNode()) == null ? void 0 : _a.classList.add("is-single-slide");
    } else {
      emblaWrapper.classList.remove("is-single-slide");
      (_b = emblaApi.containerNode()) == null ? void 0 : _b.classList.remove("is-single-slide");
    }
  };
  const onDestroy = () => {
    var _a;
    emblaWrapper.classList.remove("is-initialized", "is-single-slide");
    (_a = emblaApi.containerNode()) == null ? void 0 : _a.classList.remove("is-single-slide");
  };
  emblaApi.on("init", onInit).on("reinit", onInit);
  return () => {
    emblaApi.off("init", onInit).off("reinit", onInit);
    onDestroy();
  };
};
const duplicateSlidesForLoop = (emblaApi, emblaContainer, maxIterations = 5) => {
  var _a, _b;
  const originalSlideCount = emblaContainer.children.length;
  let iterations = 0;
  while (!((_b = (_a = emblaApi == null ? void 0 : emblaApi.internalEngine()) == null ? void 0 : _a.slideLooper) == null ? void 0 : _b.canLoop()) && iterations < maxIterations) {
    const currentSlides = Array.from(emblaContainer.children);
    currentSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.querySelectorAll("img").forEach((img) => {
        img.setAttribute("loaded", "");
        if (img.parentElement) img.parentElement.removeAttribute("data-lazy-loading");
      });
      emblaContainer.appendChild(clone);
    });
    emblaApi == null ? void 0 : emblaApi.reInit();
    iterations++;
  }
  return originalSlideCount;
};
const extractSlideColorSchemes = (emblaContainer) => {
  const slides = Array.from(emblaContainer.children);
  const colorSchemes = slides.map((slide) => {
    const colorClass = Array.from(slide.classList).find((cls) => cls.startsWith("color-"));
    return colorClass || null;
  });
  return colorSchemes;
};
const addColorSchemeSyncHandlers = (emblaApi, paginationContainer, slideColorSchemes, originalSlideCount = null) => {
  if (!emblaApi || !paginationContainer || !slideColorSchemes || slideColorSchemes.length === 0) {
    return () => {
    };
  }
  let currentColorScheme = null;
  const updateColorScheme = () => {
    const selectedIndex = emblaApi.selectedScrollSnap();
    const originalIndex = originalSlideCount ? selectedIndex % originalSlideCount : selectedIndex;
    const newColorScheme = slideColorSchemes[originalIndex];
    if (newColorScheme === currentColorScheme) {
      return;
    }
    if (currentColorScheme) {
      paginationContainer.classList.remove(currentColorScheme);
    }
    if (newColorScheme) {
      paginationContainer.classList.add(newColorScheme);
    }
    currentColorScheme = newColorScheme;
  };
  emblaApi.on("init", updateColorScheme).on("reinit", updateColorScheme).on("select", updateColorScheme);
  return () => {
    emblaApi.off("init", updateColorScheme).off("reinit", updateColorScheme).off("select", updateColorScheme);
  };
};
const addNumberPagination = (emblaApi, paginationContainer) => {
  if (!emblaApi || !paginationContainer) {
    return () => {
    };
  }
  const currentEl = paginationContainer.querySelector(".embla__number-current");
  const totalEl = paginationContainer.querySelector(".embla__number-total");
  const prevBtn = paginationContainer.querySelector(".embla__number-nav--prev");
  const nextBtn = paginationContainer.querySelector(".embla__number-nav--next");
  if (!currentEl || !totalEl) {
    return () => {
    };
  }
  const updateNumbers = () => {
    const currentSlide = emblaApi.selectedScrollSnap() + 1;
    const totalSlides = emblaApi.scrollSnapList().length;
    currentEl.textContent = currentSlide;
    totalEl.textContent = totalSlides;
  };
  const toggleNavButtons = () => {
    if (prevBtn) {
      if (emblaApi.canScrollPrev()) {
        prevBtn.removeAttribute("disabled");
      } else {
        prevBtn.setAttribute("disabled", "disabled");
      }
    }
    if (nextBtn) {
      if (emblaApi.canScrollNext()) {
        nextBtn.removeAttribute("disabled");
      } else {
        nextBtn.setAttribute("disabled", "disabled");
      }
    }
  };
  const scrollPrev = () => emblaApi.scrollPrev();
  const scrollNext = () => emblaApi.scrollNext();
  if (prevBtn) prevBtn.addEventListener("click", scrollPrev);
  if (nextBtn) nextBtn.addEventListener("click", scrollNext);
  updateNumbers();
  toggleNavButtons();
  emblaApi.on("select", updateNumbers).on("select", toggleNavButtons).on("init", updateNumbers).on("init", toggleNavButtons).on("reinit", updateNumbers).on("reinit", toggleNavButtons);
  return () => {
    if (prevBtn) prevBtn.removeEventListener("click", scrollPrev);
    if (nextBtn) nextBtn.removeEventListener("click", scrollNext);
    emblaApi.off("select", updateNumbers).off("select", toggleNavButtons).off("init", updateNumbers).off("init", toggleNavButtons).off("reinit", updateNumbers).off("reinit", toggleNavButtons);
  };
};
const addThumbButtonClickHandlers = (emblaApiMain, emblaApiThumb) => {
  const slidesThumbs = emblaApiThumb.slideNodes();
  const scrollToIndex = slidesThumbs.map(
    (_, index) => () => emblaApiMain.scrollTo(index)
  );
  slidesThumbs.forEach((slideNode, index) => {
    slideNode.addEventListener("click", scrollToIndex[index], false);
  });
};
const addToggleThumbButtonsActive = (emblaApiMain, emblaApiThumb) => {
  const slidesThumbs = emblaApiThumb.slideNodes();
  const toggleThumbBtnsState = () => {
    emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap());
    const previous = emblaApiMain.previousScrollSnap();
    const selected = emblaApiMain.selectedScrollSnap();
    slidesThumbs[previous].classList.remove("embla-thumbs__slide--selected");
    slidesThumbs[selected].classList.add("embla-thumbs__slide--selected");
  };
  emblaApiMain.on("select", toggleThumbBtnsState);
  toggleThumbBtnsState();
};
export {
  addPrevNextBtnsClickHandlersSSR as a,
  addDotsBtnsAndClickHandlersSSR as b,
  addInstagramDotBtnsAndClickHandlers as c,
  duplicateSlidesForLoop as d,
  extractSlideColorSchemes as e,
  addLayoutShiftPrevention as f,
  addColorSchemeSyncHandlers as g,
  addNumberPagination as h,
  addThumbButtonClickHandlers as i,
  addToggleThumbButtonsActive as j,
  addBarsBtnsAndClickHandlersSSR as k
};
